var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import { Box } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { makeStyles, createStyles, fade } from '@material-ui/core/styles';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ClearIcon from '@material-ui/icons/Clear';
import DoneIcon from '@material-ui/icons/Done';
import CircularProgress from '@material-ui/core/CircularProgress';
import { KEY_CODES } from '../../common/types/keyCodes';
import { SubmittableStatus } from './types';
var useButtonStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            color: theme.palette.primary.main,
            borderColor: theme.commonSettings.borderColor,
            maxWidth: '25px',
            minWidth: '25px',
            maxHeight: '25px',
            padding: '0',
            borderRadius: '0px 3px 3px 0px',
            fontSize: '1.3rem',
            '&:hover': { backgroundColor: fade(theme.palette.primary.light, 0.3) },
        },
    });
});
var useButtonGroupStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            '& > *': {
                borderColor: theme.commonSettings.borderColor,
                fontSize: '1rem',
            },
            '& > *:hover': { backgroundColor: fade(theme.palette.primary.light, 0.3) },
        },
    });
});
var useBoxStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            boxSizing: 'border-box',
            height: '25px',
            paddingLeft: '4px',
            overflow: 'hidden',
            border: "1px solid ".concat(theme.commonSettings.borderColor),
            borderRightWidth: '0px',
            borderRadius: '3px 0px 0px 3px',
            alignItems: 'center',
        },
    });
});
function SubmittableInputActive(props) {
    var defaultValue = props.defaultValue, children = props.children, action = props.action, getCalculatedValue = props.getCalculatedValue, setDisplay = props.setDisplay;
    var _a = useState(SubmittableStatus.Active), status = _a[0], setStatus = _a[1];
    var _b = useState(defaultValue), inputValue = _b[0], setInputValue = _b[1];
    var buttonClasses = useButtonStyles(props);
    var boxClasses = useBoxStyles(props);
    var buttonGroupClasses = useButtonGroupStyles(props);
    // reference to the wrapper div
    var containerRef = useRef(null);
    var didMountRef = useRef(false);
    useEffect(function () {
        if (didMountRef.current) {
            setDisplay(defaultValue);
        }
        else
            didMountRef.current = true;
        return function () {
            didMountRef.current = false;
        };
    });
    useEffect(function () {
        if (!defaultValue) {
            setInputValue(getCalculatedValue());
        }
        containerRef.current.querySelector('input').focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var triggerAction = useCallback(function () {
        if (status === SubmittableStatus.Loading)
            return;
        if (defaultValue === inputValue) {
            setDisplay(defaultValue);
            return;
        }
        setStatus(SubmittableStatus.Loading);
        action(inputValue)
            .then(function (response) {
            setDisplay(inputValue);
            if (!response) {
                setDisplay(defaultValue);
                setInputValue(defaultValue);
            }
        })
            .catch(function () {
            setDisplay(defaultValue);
            setInputValue(defaultValue);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, defaultValue, inputValue, status]);
    var onKeyDown = function (event) {
        // if it's escape then cancel
        if (event.key === KEY_CODES.ESCAPE.key || event.keyCode === KEY_CODES.ESCAPE.code) {
            setDisplay(defaultValue);
        }
        // if it's enter then call the action
        if (event.key === KEY_CODES.ENTER.key || event.keyCode === KEY_CODES.ENTER.code)
            triggerAction();
    };
    var renderIconButton = useCallback(function () {
        if (status === SubmittableStatus.Active) {
            return (React.createElement(ButtonGroup, { color: 'primary', "aria-label": 'outlined primary button group', className: buttonGroupClasses.root },
                React.createElement(Button, { style: { borderRadius: '0px' }, onMouseDown: function () { return setDisplay(defaultValue); }, className: buttonClasses.root },
                    React.createElement(ClearIcon, { fontSize: 'inherit' })),
                React.createElement(Button, { onClick: triggerAction, className: buttonClasses.root },
                    React.createElement(DoneIcon, { fontSize: 'inherit' }))));
        }
        if (status === SubmittableStatus.Loading) {
            return (React.createElement(Button, { variant: 'outlined', color: 'secondary', disabled: true, className: buttonClasses.root },
                React.createElement(CircularProgress, { size: '12px' })));
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buttonClasses.root, buttonGroupClasses.root, status, triggerAction]);
    return (React.createElement("div", { tabIndex: -1, style: { display: 'inline-flex', maxWidth: '120px', backgroundColor: '#ffffff' }, onBlur: triggerAction, ref: containerRef },
        React.createElement("div", { tabIndex: 0 }),
        React.createElement(Box, { className: boxClasses.root }, React.cloneElement(children, {
            value: inputValue,
            onFocus: function (event) { return setTimeout(function () { return event.target.select(); }); },
            onValueChange: setInputValue,
            onKeyDown: onKeyDown,
            disabled: status === SubmittableStatus.Loading,
        })),
        renderIconButton()));
}
/**
 * @deprecated
 * Use `SubmittableInput` to render any input that has an action to submit. It will submit also when focusing out
 */
function SubmittableInput(props) {
    var defaultValue = props.defaultValue, children = props.children;
    var _a = useState(defaultValue), inputValue = _a[0], setInputValue = _a[1];
    var _b = useState(false), selected = _b[0], setSelected = _b[1];
    var _c = useState(true), showDisplay = _c[0], setShowDisplay = _c[1];
    var buttonClasses = useButtonStyles(props);
    var boxClasses = useBoxStyles(props);
    var buttonGroupClasses = useButtonGroupStyles(props);
    var setDisplay = function (val) {
        setInputValue(val);
        setShowDisplay(true);
    };
    return showDisplay ? (React.createElement("div", { role: 'textbox', tabIndex: -1, style: { display: 'inline-flex', maxWidth: '120px' }, onFocus: function () {
            setShowDisplay(false);
        }, onClick: function () {
            setShowDisplay(false);
        }, onKeyDown: function () {
            setShowDisplay(false);
        } },
        React.createElement("div", { tabIndex: 0 }),
        React.createElement(Box, { className: boxClasses.root, onFocus: function (event) {
                event.stopPropagation();
            }, onClick: function (e) {
                if (selected)
                    e.stopPropagation();
            } }, React.cloneElement(children, {
            value: inputValue,
            readOnly: true,
            style: { display: 'inline-flex', maxWidth: '120px' },
            onSelect: function (e) {
                setSelected(e.target.selectionEnd - e.target.selectionStart !== 0);
            },
        })),
        React.createElement(ButtonGroup, { color: 'primary', "aria-label": 'outlined primary button group', className: buttonGroupClasses.root },
            React.createElement(Button, { onFocus: function (event) {
                    event.stopPropagation();
                }, onClick: function () { return setShowDisplay(false); }, className: buttonClasses.root },
                React.createElement(EditIcon, { fontSize: 'inherit' }))))) : (React.createElement(SubmittableInputActive, __assign({}, props, { defaultValue: inputValue, setDisplay: setDisplay })));
}
export default SubmittableInput;
//# sourceMappingURL=SubmittableInput.js.map